import { mdiCheck, mdiClose, mdiPencil, mdiDelete, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  itemStockOutwardDefaultValue,
  OutwardSource,
  StockOutwardItemMovementModel,
  UniqueProductModel,
} from "../models/stock_transfer_model";
import { WarehouseModel } from "../models/warehouse_model";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { generate_id } from "../journals/destination_stock_voucher/components/item_row_status";
import { fetchGet } from "../../../service/api_client";
import ModalDialog from "../../../ui/new_components/common/modal";
import { ItemModelV2 } from "../models/item_model";
import NewItemSearchByIdField from "../items/item_search_by_ids";
import POItemSearchFieldV2 from "../../purchase_order/purchase_order_create/components/po-item-search-widget";

interface ItemRowProps {
  component?: StockOutwardItemMovementModel;
  onSubmit: (data: StockOutwardItemMovementModel) => void;
  edit: boolean;
  disabled: boolean;
  onDelete?: (i: StockOutwardItemMovementModel) => void;
  existingItems: StockOutwardItemMovementModel[];
  availableItems?: any;
  from_warehouse: WarehouseModel;
  // in_transit_id?: string;
  source: string;
}

const ItemRowViewOutwardScreen: FunctionComponent<ItemRowProps> = (props) => {
  const [data, setData] = useState<StockOutwardItemMovementModel>({
    ...itemStockOutwardDefaultValue,
  });
  const { showToast } = useToast();
  const { edit, onSubmit, existingItems, availableItems } = props;
  const [editable, setEditable] = useState(false);
  const [addProduct, setAddProduct] = useState(false);
  const [addProduct1, setAddProduct1] = useState(false);
  const [check, setMdiCheck] = useState(true);
  const [, setLoading] = useState(false);
  const [totalUnits, setTotalUnits] = useState(0);
  const [prevUnits, setPrevUnits] = useState(0);
  const location = useLocation();
  const isCreatePath = location.pathname.endsWith("/create");

  const uniqueIds = useRef<any[]>([]);
  const itemIdsRef = useRef<any[]>([]);
  const itemIds = availableItems
    ? availableItems?.map((item: any) => item.item_id) || []
    : "";

  useEffect(() => {
    itemIdsRef.current = availableItems
      ? availableItems.map((item: any) => item.item_id)
      : "";
    setEditable(edit);
    if (props.component) {
      setData(props.component);
    } else {
      setData({ ...itemStockOutwardDefaultValue });
    }
  }, [edit, props.component, availableItems]);

  useEffect(() => {
    if (props.component) {
      setData((prevData) => ({
        ...prevData,
        ...props.component,
      }));
    }
  }, [props.component]);

  const [initialRows, setInitialRows] = useState<
    { id: string; unique_id: string; item_name: string }[]
  >([]);

  useEffect(() => {
    // Find the existing item in the list
    const existingItem = existingItems.find((item) => item.id === data.id);

    // Get the existing unit_no, defaulting to 0 if not found
    const existingUnitNo = existingItem ? Number(existingItem.unit_no) || 0 : 0;

    // Update state with the existing unit_no
    setPrevUnits(existingUnitNo);
  }, [existingItems, data.id]); // Track changes in existingItems and data.id

  useEffect(() => {
    if (props.source === OutwardSource["Stock inventory"]) {
      if (!data.unit_no) return; // Ensure unit_no is valid
      if (
        data.item?.subcategory !== "Finished Goods" &&
        data.item?.subcategory !== "IT and Electronic Hardware"
      ) {
        return;
      }
      if (!data.unit_no) return; // Ensure unit_no is valid

      const rows = Array.from({ length: data.unit_no }, (_, index) => {
        const existingUniqueId = data.uniqueIds?.[index]?.unique_id || ""; // Get existing unique_id if present

        return {
          id: `${"OUT"}-${generate_id(8, "numeric")}`, // Unique ID generation
          unique_id: existingUniqueId,
          item_name: data.item?.name || "",
        };
      });

      setInitialRows(rows);
      setData((prevData) => ({ ...prevData, uniqueIds: rows }));
    }
  }, [data.unit_no, props.source]); // Depend on data.unit_no to update rows dynamically

  // Use data.uniqueIds as the source of truth
  const handleInputChange = (index: number, value: string) => {
    const updatedUniqueIds: UniqueProductModel[] = Array.isArray(data.uniqueIds)
      ? [...data.uniqueIds]
      : [];
    updatedUniqueIds[index].unique_id = value;
    setData({ ...data, uniqueIds: updatedUniqueIds });
  };

  useEffect(() => {
    if (data.item?.item_code && props.from_warehouse?.name) {
      checkUniqueIdsExists();
    }
  }, [data.item?.item_code, props.from_warehouse?.name]);

  const checkUniqueIdsExists = async () => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/unique-ids?warehouse_id=${props.from_warehouse?.id}&item_code=${data.item?.item_code}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);
      console.log("result after uniqueids:", res);
      if (res.success) {
        // Later in checkUniqueIdsExists:
        uniqueIds.current = res.result?.map((item: any) => item.unique_id);
        console.log("unique ids:", uniqueIds);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching uniqueIds",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Add new row
  const handleAdd = () => {
    const newRow: UniqueProductModel = {
      unique_id: "",
      item_name: data.item?.name || "",
    };

    setData({
      ...data,
      uniqueIds: [...(data.uniqueIds || []), newRow],
    });
  };

  // Delete row
  const handleDelete = (index: number) => {
    const updatedUniqueIds = [...(data.uniqueIds || [])];
    updatedUniqueIds.splice(index, 1);
    setData({ ...data, uniqueIds: updatedUniqueIds });
  };

  const handleItemSelect = async (selectedItem: ItemModelV2) => {
    if (props.from_warehouse?.name === undefined) {
      showToast({
        type: ToastType.warning,
        text: "Please select from warehouse before entering item.",
      });
      return;
    }
    if (data.unit_no) {
      setData((prevData) => ({
        ...prevData,
        unit_no: 0, // Reset units when a new item is selected
      }));
    }

    // Find available units from availableItems using selected item ID and warehouse ID
    const available = availableItems?.find(
      (item: any) =>
        item.item_id === selectedItem.id &&
        item.warehouse_id === props.from_warehouse?.id
    );

    const availableUnits = available?.available_units ?? 0;
    setTotalUnits(availableUnits); // <-- Set available units here

    setData((prevData) => ({
      ...prevData,
      id: generate_id(),
      item: selectedItem,
      item_id: selectedItem.id,
      item_code: selectedItem.item_code,
      unit_measure: selectedItem.unit?.formal_name,
    }));
  };

  useEffect(() => {
    if (data.inTransit_id && props.from_warehouse?.name) {
      handleIntransit();
    }
  }, [data.inTransit_id, props.from_warehouse?.name]);

  const handleIntransit = async () => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/intransit?from_warehouse=${props.from_warehouse?.name}&in_transit_id=${data.inTransit_id}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);
      console.log("result:", res);

      if (res.success) {
        const { item_data } = res.data;
        console.log("item_data after in_transit:", res.data);
        const filteredUniqueIds = item_data?.uniqueIds?.filter(
          (u: any) => u.status === "Confirm"
        );

        setData((prev) => ({
          ...prev,
          stock_transfer_id: item_data?.stock_transfer_id,
          item_code: item_data?.item_code,
          item: item_data?.item,
          uniqueIds: filteredUniqueIds,
        }));

        setTotalUnits(item_data?.in_transit_units);
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching warehouse stock value",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updatePrice = async (requestedUnits: number) => {
    if (requestedUnits > totalUnits) {
      showToast({
        type: ToastType.warning,
        text: "Requested units exceed available stock.",
      });
      return;
    } else {
      setData((prevData) => ({
        ...prevData,
        unit_no: requestedUnits,
      }));
    }
  };

  const updatePriceOfIntransit = async (requestedUnits: number) => {
    if (requestedUnits > totalUnits) {
      showToast({
        type: ToastType.warning,
        text: "Requested units exceed available stock.",
      });
      return;
    } else {
      setData((prevUnits) => {
        return {
          ...prevUnits,
          unit_no: requestedUnits,
        };
      });
    }
  };

  useEffect(() => {
    if (data.uniqueIds) {
      setData((prevData) => ({
        ...prevData,
        uniqueIds: data.uniqueIds,
      }));
    }
  }, [data.uniqueIds]);

  const onDone = () => {
    if (data.uniqueIds) {
      const hasEmpty = data.uniqueIds.some(
        (object) => !object.unique_id?.trim()
      );
      if (hasEmpty) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid Unique ID for all rows",
        });
        return;
      }
    }

    // Check for duplicates in previous entries
    const isDuplicate = (existingItems ?? []).some(
      (entry) =>
        entry.item?.item_code === data.item?.item_code && entry.id !== data.id // ignore current row being edited
    );

    if (isDuplicate) {
      showToast({
        type: ToastType.warning,
        text: "Item already added for same item code!!",
      });
      return;
    }
    if (
      data.item?.subcategory !== "Finished Goods" &&
      data.item?.subcategory !== "IT and Electronic Hardware"
    ) {
      delete data.uniqueIds;
    } else if (data.uniqueIds) {
      for (const object of data.uniqueIds) {
        if (!object.unique_id) {
          showToast({
            type: ToastType.error,
            text: "Please enter a valid Item Unique ID",
          });
          return; // This will stop further execution of the parent function
        }
      }
    }

    if (
      !data.item ||
      !data.unit_no ||
      !props.from_warehouse?.name ||
      !props.source
    ) {
      showToast({
        type: ToastType.warning,
        text: "Please select Item, Warehouse, and source",
      });
      setEditable(true);
      return;
    }
    console.log("data getting ", data);
    onSubmit(data);
  };

  const handleAddingUniqueIds = async () => {
    if (!Array.isArray(data.uniqueIds)) return;

    for (const object of data.uniqueIds) {
      if (!object.unique_id || object.unique_id.trim() === "") {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid Unique ID, dont keep empty field!!",
        });
        return;
      } else if (!uniqueIds.current.includes(object.unique_id)) {
        showToast({
          type: ToastType.error,
          text: `Unique ID does not exist for ${object.unique_id}`,
        });
        return;
      }
    }

    // If loop completes without triggering any return
    setAddProduct(false);
  };

  return (
    <div>
      {setAddProduct1 && (
        <ModalDialog
          show={addProduct1}
          title={"Product Details"}
          onClose={() => {
            setAddProduct1(false);
            setEditable(true);
            // setData((prevData) => ({ ...prevData, uniqueIds: initialRows }));
          }}
        >
          <div className="bg-white p-6 w-96">
            <div className="flex justify-between">
              <h2 className="text-md font-semibold mb-4">Product List</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-300">
                {/* Table Head */}
                <thead className="bg-gray-200">
                  <tr>
                    <th className="p-2 border">Unique Id</th>
                    <th className="p-2 border">Item Name</th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody>
                  {(data.uniqueIds || []).map((row: any, index: number) => (
                    <tr key={row.id} className="text-center">
                      <td className="p-2 border">
                        <input
                          type="text"
                          value={row.unique_id || ""}
                          className="p-1 border rounded w-full focus:outline-none"
                          placeholder="Enter Unique Id"
                        />
                      </td>
                      <td className="p-2 border">{row.item_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ModalDialog>
      )}
      {setAddProduct && (
        <ModalDialog
          show={addProduct}
          title={"Product Details"}
          onClose={() => {
            setAddProduct(false);
            setData((prevData) => ({ ...prevData, uniqueIds: initialRows }));
          }}
        >
          <div className="bg-white p-6 w-96">
            <p>Avail Devices: {(uniqueIds.current || []).join(", ")}</p>

            <div className="flex justify-between">
              <h2 className="text-md font-semibold mb-4">Add Product List</h2>
              <div className="">
                <button className="bg-myPrimaryColor text-white p-1 rounded-md">
                  Upload
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-60 border border-gray-300">
                <table className="w-full border border-gray-300">
                  {/* Table Head */}
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="p-2 border">Unique Id</th>
                      <th className="p-2 border">Item Name</th>
                      <th className="p-2 border">Actions</th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody>
                    {/* {rows.map((row: any, index: any) => ( */}
                    {(data.uniqueIds || []).map((row: any, index) => (
                      <tr key={row.id} className="text-center">
                        <td className="p-2 border">
                          <input
                            type="text"
                            value={row.unique_id || ""}
                            onChange={(e) =>
                              handleInputChange(index, e.target.value)
                            }
                            className="p-1 border rounded w-full focus:outline-none"
                            placeholder="Enter Unique Id"
                          />
                        </td>

                        <td className="p-2 border">{row.item_name}</td>
                        <td className="p-2 border">
                          <div
                            onClick={() => handleDelete(index)}
                            className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer inline-block"
                          >
                            <Icon path={mdiDelete} className="h-4 w-4" />
                          </div>
                        </td>
                      </tr>
                    ))}

                    {/* Add New Row Button */}
                    {/* <tr>
                      <td colSpan={3} className="p-2 border text-center">
                        <div
                          onClick={handleAdd}
                          className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200 cursor-pointer inline-flex items-center space-x-1"
                        >
                          <Icon path={mdiPlus} className="h-4 w-4" />
                          <span>Add Row</span>
                        </div>
                      </td>
                    </tr> */}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-3 sticky bottom-0 bg-white z-10 p-2 border-t">
                <button
                  className="bg-myPrimaryColor text-white p-1 rounded-md mt-4 "
                  onClick={() => {
                    handleAddingUniqueIds();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </ModalDialog>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onDone();
        }}
        className="bg-white p-1"
      >
        {props.source === OutwardSource["Stock inventory"] && (
          <div className="grid grid-cols-9 gap-1 items-center">
            <div>
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="item-code"
                disabled
                value={data.item_code}
                readOnly
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>

            <div className="col-span-2">
              <NewItemSearchByIdField
                disabled={!editable || props.disabled}
                value={data.item}
                item_ids={itemIds}
                onSelect={handleItemSelect}
                // onSelect={(d: ItemModelV2) => {
                //   // setSelectedItem(data.item);
                //   setData((o) => ({
                //     ...o,
                //     item: d,
                //     item_id: d.id,
                //     item_code: d.item_code,
                //   }));
                // }}
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div>
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="unit"
                disabled
                value={data.unit_measure}
                readOnly
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div>
              <input
                value={data.unit_no || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const unitNo = Number(e.target.value) || 0;
                  // setData((prevData) => ({
                  //   ...prevData,
                  //   unit_no: unitNo,
                  // }));
                  if (props.from_warehouse) {
                    updatePrice(unitNo);
                  }
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                Avail Units:{totalUnits}
              </span>
            </div>
            <div>
              {data.item?.type === "Product" &&
              (data.item?.subcategory === "Finished Goods" ||
                data.item?.subcategory === "IT and Electronic Hardware") ? (
                <div
                  onClick={() => {
                    setAddProduct(true);
                  }}
                  className="text-left p-1 focus:outline-none border rounded text-blue-800 w-full cursor-pointer"
                >
                  <div className="flex items-center space-x-2">
                    <Icon path={mdiPlus} className="h-6 w-4" />
                    {/* <span>
                      {(data.uniqueIds?.length ?? 0) > 0
                        ? `${data.uniqueIds?.length ?? 0} items added`
                        : "Add/Upload"}
                    </span> */}
                    <span>
                      {(data.uniqueIds?.filter((u) => u.unique_id)?.length ??
                        0) > 0
                        ? `${
                            data.uniqueIds?.filter((u) => u.unique_id)
                              ?.length ?? 0
                          } devices added`
                        : "Add/Upload"}
                    </span>
                  </div>
                </div>
              ) : (
                <input
                  className="text-left p-1 focus:outline-none border rounded w-full"
                  placeholder="unique"
                  // value={}
                  onClick={() => {}}
                />
              )}
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>

            <div>
              <input
                value={data.price || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const price = Number(e.target.value) || 0;
                  setData((prevData) => ({
                    ...prevData,
                    price: price,
                  }));
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                {/* Price of latest:{totalUnits} */}&nbsp;
              </span>
            </div>
            <div>
              <input
                value={data.price * data.unit_no || ""}
                disabled={!editable || props.disabled}
                // disabled={!editable || props.disabled}
                type="text"
                readOnly
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div className="flex gap-2 top-0 items-center justify-center">
              {props.component ? (
                !props.disabled && (
                  <div>
                    <div className="flex justify-center gap-2">
                      {editable ? (
                        <>
                          <div
                            onClick={() => {
                              if (!window.confirm("Are you sure!")) return;
                              onDone();
                              setEditable(false);
                            }}
                            className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                          >
                            <Icon path={mdiCheck} className="h-4 w-4" />
                          </div>
                          <div
                            onClick={() => {
                              setData(
                                props.component || itemStockOutwardDefaultValue
                              );
                              setEditable(false);
                            }}
                            className="bg-gray-100 p-1 rounded text-gray-800 hover:scale-110 transform duration-200"
                          >
                            <Icon path={mdiClose} className="h-4 w-4" />
                          </div>
                        </>
                      ) : (
                        (data.voucher_id || isCreatePath) && (
                          <div
                            onClick={() => {
                              setEditable(true);
                              setMdiCheck(false);
                            }}
                            className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                          >
                            <Icon path={mdiPencil} className="h-4 w-4" />
                          </div>
                        )
                      )}
                      {/* {data.voucher_id && ( */}
                      <div
                        onClick={() =>
                          props.onDelete && props.onDelete(props.component!)
                        }
                        className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                      >
                        <Icon path={mdiDelete} className="h-4 w-4" />
                      </div>
                      {/* )} */}
                    </div>
                    <span className="text-xs text-gray-500">&nbsp;</span>
                  </div>
                )
              ) : (
                <div>
                  <div
                    onClick={() => {
                      setEditable(false);
                      onDone();
                    }}
                    className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                  >
                    <Icon path={mdiPlus} className="h-4 w-4" />
                  </div>
                  <span className="text-xs text-gray-500">&nbsp;</span>
                </div>
              )}
            </div>
          </div>
        )}
        {props.source === OutwardSource["In-transit"] && (
          <div className="grid grid-cols-10 gap-1 items-center">
            <div>
              <input
                value={data.inTransit_id || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const id = e.target.value;
                  setData((prevData) => ({
                    ...prevData,
                    inTransit_id: id,
                  }));
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div>
              <input
                value={data.stock_transfer_id || ""}
                disabled={!editable || props.disabled}
                // onChange={(e) => {
                //   const id = e.target.value;
                //   setData((prevData) => ({
                //     ...prevData,
                //     inTransit_id: id,
                //   }));
                // }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div>
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="item-code"
                disabled
                value={data.item_code}
                readOnly
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>

            <div className="col-span-2">
              <POItemSearchFieldV2
                disabled={!editable || props.disabled}
                value={data.item}
                // item_ids={itemIds}
                onSelect={(d: ItemModelV2) => {
                  // setSelectedItem(data.item);
                  setData((o) => ({
                    ...o,
                    item: d,
                    item_id: d.id,
                    item_code: d.item_code,
                  }));
                }}
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>

            <div>
              {data.item?.type === "Product" &&
              (data.item?.subcategory === "Finished Goods" ||
                data.item?.subcategory === "IT and Electronic Hardware") ? (
                <div
                  onClick={() => {
                    setAddProduct1(true);
                  }}
                  className="text-left p-1 focus:outline-none border rounded text-blue-800 w-full cursor-pointer"
                >
                  <div className="flex items-center space-x-2">
                    <Icon path={mdiPlus} className="h-6 w-4" />
                    {/* <span>
                      {(data.uniqueIds?.length ?? 0) > 0
                        ? `${data.uniqueIds?.length ?? 0} items added`
                        : "Add/Upload"}
                    </span> */}
                    <span>
                      {(data.uniqueIds?.filter((u) => u.status === "Confirm")
                        ?.length ?? 0) > 0
                        ? `${
                            data.uniqueIds?.filter(
                              (u) => u.status === "Confirm"
                            )?.length ?? 0
                          } devices added`
                        : "Add/Upload"}
                    </span>
                  </div>
                </div>
              ) : (
                <input
                  className="text-left p-1 focus:outline-none border rounded w-full"
                  placeholder="unique"
                  // value={}
                  onClick={() => {}}
                />
              )}
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            {/* {props.source === OutwardSource["Stock inventory"] && ( */}
            <div>
              <input
                value={data.unit_no || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const unitNo = Number(e.target.value) || 0;
                  // setData((prevData) => ({
                  //   ...prevData,
                  //   unit_no: unitNo,
                  // }));
                  if (props.from_warehouse) {
                    updatePriceOfIntransit(unitNo);
                  }
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                Intransit Units:{totalUnits}
              </span>
            </div>
            {/* )} */}
            {/* {props.source === OutwardSource["In-transit"] && (
            <div>
              <input
                value={data.unit_no || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const unitNo = Number(e.target.value) || 0;
                  // setData((prevData) => ({
                  //   ...prevData,
                  //   unit_no: unitNo,
                  // }));
                  if (props.from_warehouse) {
                    updatePriceBasedOnUnits(unitNo);
                  }
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                Avail Units:{totalUnits}
              </span>
            </div>
          )} */}
            <div>
              <input
                value={data.price || ""}
                disabled={!editable || props.disabled}
                onChange={(e) => {
                  const price = Number(e.target.value) || 0;
                  setData((prevData) => ({
                    ...prevData,
                    price: price,
                  }));
                }}
                // disabled={!editable || props.disabled}
                type="text"
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">
                {/* Price of latest:{totalUnits} */}&nbsp;
              </span>
            </div>
            <div>
              <input
                value={data.price * data.unit_no || ""}
                disabled={!editable || props.disabled}
                // disabled={!editable || props.disabled}
                type="text"
                readOnly
                className="text-left p-1 focus:outline-none border rounded w-full"
              />
              <span className="text-xs text-gray-500">&nbsp;</span>
            </div>
            <div className="flex gap-2 top-0 items-center justify-center">
              {props.component ? (
                !props.disabled && (
                  <div className="flex justify-center gap-2">
                    {editable ? (
                      <>
                        <div
                          onClick={() => {
                            if (!window.confirm("Are you sure!")) return;
                            onDone();
                            setEditable(false);
                          }}
                          className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiCheck} className="h-4 w-4" />
                        </div>
                        <div
                          onClick={() => {
                            setData(
                              props.component || itemStockOutwardDefaultValue
                            );
                            setEditable(false);
                          }}
                          className="bg-gray-100 p-1 rounded text-gray-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiClose} className="h-4 w-4" />
                        </div>
                      </>
                    ) : (
                      (data.voucher_id || isCreatePath) && (
                        <div
                          onClick={() => {
                            setEditable(true);
                            setMdiCheck(false);
                          }}
                          className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                        >
                          <Icon path={mdiPencil} className="h-4 w-4" />
                        </div>
                      )
                    )}
                    {/* {data.voucher_id && ( */}
                    <div
                      onClick={() =>
                        props.onDelete && props.onDelete(props.component!)
                      }
                      className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                    >
                      <Icon path={mdiDelete} className="h-4 w-4" />
                    </div>
                    {/* )} */}
                  </div>
                )
              ) : (
                <div
                  onClick={() => {
                    setEditable(false);
                    onDone();
                  }}
                  className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200"
                >
                  <Icon path={mdiPlus} className="h-4 w-4" />
                </div>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ItemRowViewOutwardScreen;
